import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIsMobile } from '@/hooks/useMediaQuery';

/**
 * Custom hook to synchronize component state with URL search parameters
 * Enables state persistence across page reloads and demo mode toggles
 */

export interface DashboardUrlState {
  selectedUnitName?: string; // Unit ID like "1RE001TCA"
  unitSlug?: string; // Semantic slug like "film-1RE001TCA" or "wb-batman-la-1RE001TCA"
  selectedTab?: 'solar' | 'charger' | 'hvac' | 'starlink' | 'aqi';
  viewMode?: 'map' | 'detail' | 'gallery';
  searchQuery?: string;
  statusFilters?: Set<'online' | 'offline' | 'idle' | 'unknown'>;
  showAlerts?: 'none' | 'both' | 'warnings' | 'faults';
  mobileTab?: 'map' | 'list' | 'detail';
  viewport?: string; // 'true' | 'false' - viewport filter state
}

export function useUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Read initial state from URL
  const getInitialState = useCallback((): DashboardUrlState => {
    const state: DashboardUrlState = {};

    // Parse unit identifier from URL
    // Expected format: ?unit=unit-code (e.g. ?unit=1RE001TCA)
    const unitSlug = searchParams.get('unit');
    if (unitSlug) {
      // Store the slug directly, we'll resolve it to a unit object in the Dashboard component
      state.unitSlug = unitSlug;
      state.selectedUnitName = unitSlug;
    }

    // Parse selected tab
    const tab = searchParams.get('tab');
    if (tab && ['solar', 'charger', 'hvac', 'starlink', 'aqi'].includes(tab)) {
      state.selectedTab = tab as 'solar' | 'charger' | 'hvac' | 'starlink' | 'aqi';
    }

    // Parse view mode
    const view = searchParams.get('view');
    if (view && ['map', 'detail', 'gallery'].includes(view)) {
      state.viewMode = view as 'map' | 'detail' | 'gallery';
    }

    // Parse search query
    const search = searchParams.get('search');
    if (search) state.searchQuery = search;

    // Parse status filters (comma-separated)
    const status = searchParams.get('status');
    if (status) {
      const filters = status.split(',').filter(s =>
        ['online', 'offline', 'idle', 'unknown'].includes(s)
      ) as Array<'online' | 'offline' | 'idle' | 'unknown'>;
      if (filters.length > 0) {
        state.statusFilters = new Set(filters);
      }
    }

    // Parse alert filter
    const alerts = searchParams.get('alerts');
    if (alerts && ['none', 'both', 'warnings', 'faults'].includes(alerts)) {
      state.showAlerts = alerts as 'none' | 'both' | 'warnings' | 'faults';
    }

    // Parse mobile tab
    const mTab = searchParams.get('mtab');
    if (mTab && ['map', 'list', 'detail'].includes(mTab)) {
      state.mobileTab = mTab as 'map' | 'list' | 'detail';
    }

    // Parse viewport filter
    const viewport = searchParams.get('viewport');
    if (viewport) {
      state.viewport = viewport;
    }

    return state;
  }, [searchParams]);

  // Update URL with current state
  const updateUrlState = useCallback((state: DashboardUrlState) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      // Update or remove unit identifier
      if (state.unitSlug) {
        newParams.set('unit', state.unitSlug);
      } else if (state.selectedUnitName) {
        newParams.set('unit', state.selectedUnitName);
      } else {
        newParams.delete('unit');
      }

      // Update or remove tab
      if (state.selectedTab) {
        newParams.set('tab', state.selectedTab);
      } else {
        newParams.delete('tab');
      }

      // Update or remove view mode (Desktop only)
      // If we are on mobile, we rely on `mtab` and clear `view` to avoid conflicts
      if (!isMobile && state.viewMode && state.viewMode !== 'map') {
        newParams.set('view', state.viewMode);
      } else {
        newParams.delete('view');
      }

      // Update or remove search query
      if (state.searchQuery && state.searchQuery.trim() !== '') {
        newParams.set('search', state.searchQuery);
      } else {
        newParams.delete('search');
      }

      // Update or remove status filters
      if (state.statusFilters && state.statusFilters.size > 0) {
        const allStatuses = new Set(['online', 'offline', 'idle', 'unknown']);
        // Only add param if not all statuses are selected (to keep URL clean)
        if (state.statusFilters.size < allStatuses.size) {
          newParams.set('status', Array.from(state.statusFilters).join(','));
        } else {
          newParams.delete('status');
        }
      } else {
        newParams.delete('status');
      }

      // Update or remove alert filter
      if (state.showAlerts && state.showAlerts !== 'none') {
        newParams.set('alerts', state.showAlerts);
      } else {
        newParams.delete('alerts');
      }

      // Update or remove mobile tab (Mobile only)
      // If we are on desktop, we rely on `view` and clear `mtab` to avoid conflicts
      if (isMobile && state.mobileTab) {
        // If map, only delete if NO unit is selected.
        // If unit is selected, we want explicit mtab=map to avoid restoration logic forcing detail view.
        if (state.mobileTab === 'map' && !state.unitSlug && !state.selectedUnitName) {
           newParams.delete('mtab');
        } else {
           newParams.set('mtab', state.mobileTab);
        }
      } else {
        newParams.delete('mtab');
      }

      // Update or remove viewport filter
      if (state.viewport === 'true') {
        newParams.set('viewport', 'true');
      } else {
        newParams.delete('viewport');
      }

      return newParams;
    }, { replace: true }); // Use replace to avoid polluting browser history
  }, [setSearchParams]);

  return {
    getInitialState,
    updateUrlState,
  };
}
